import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { CASES } from "../data/content";
import { FadeUp } from "../components/site/Reveal";
import { Eyebrow, CtaButton } from "../components/site/Primitives";
import { ThemeToggle } from "../components/theme/ThemeToggle";
import { Footer } from "../components/site/Footer";

function CaseNav() {
  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <div className="mx-auto mt-3 flex w-full max-w-[1440px] items-center justify-between px-4 md:mt-4 md:px-8">
        <div className="glass flex w-full items-center justify-between rounded-full border border-line px-4 py-2.5 md:px-5">
          <Link to="/" data-testid="case-back" className="flex items-center gap-2 text-sm font-medium text-content">
            <ArrowLeft size={17} /> <span className="hidden sm:inline">Back to</span> Work
          </Link>
          <Link to="/" aria-label="RunLoop home">
            <img src="/runloop_wordmark.png" alt="RunLoop" className="h-6 w-auto object-contain object-left brightness-0 dark:brightness-100" />
          </Link>
          <div className="flex items-center gap-2 md:gap-3">
            <ThemeToggle />
            <div className="hidden sm:block">
              <CtaButton to="/#looped-in" testid="case-cta" className="!px-5 !py-2.5">
                Let&apos;s Talk
              </CtaButton>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

/**
 * PreviewPlaceholder — a labelled empty card standing in for a product screenshot.
 * Kept in the site's engineered style: rounded-lg, hairline border, monospace label.
 */
function PreviewPlaceholder({ label, ratio = "16/9" }) {
  return (
    <div
      className="relative flex w-full items-center justify-center overflow-hidden rounded-lg border border-line bg-surface"
      style={{ aspectRatio: ratio }}
    >
      {/* fine grid overlay so it doesn't read as pure empty space */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.55]"
        style={{
          backgroundImage:
            "linear-gradient(to right, var(--border) 1px, transparent 1px), linear-gradient(to bottom, var(--border) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />
      <div className="relative flex flex-col items-center gap-2">
        <span className="h-1.5 w-1.5 rounded-full bg-brand" />
        <span className="font-mono-c text-[0.62rem] uppercase tracking-[0.22em] text-muted-c">
          {label}
        </span>
      </div>
    </div>
  );
}

/**
 * FeatureList — the eight-feature system, laid out as a spec sheet.
 * Two-column at md+, single column below. Each row has a big brand-yellow index,
 * a clear feature name and a concise description, separated by hairlines.
 * Reads more like a systems datasheet than a card grid.
 */
function FeatureList({ features }) {
  return (
    <div className="mt-14 grid grid-cols-1 md:mt-16 md:grid-cols-2 md:gap-x-14">
      {features.map((f, i) => {
        // Hairline logic: hide top border on the first row (both columns);
        // in a 2-col grid rows 1&2 are the first row of each column.
        const isFirstOfColumn = i < 2;
        return (
          <div
            key={f.n}
            className={`grid grid-cols-[auto_1fr] gap-x-6 py-8 md:gap-x-8 md:py-9 ${
              isFirstOfColumn ? "border-t border-line md:border-t" : "border-t border-line"
            }`}
          >
            <span className="font-mono-c text-[0.68rem] uppercase tracking-[0.24em] text-brand">
              {f.n}
            </span>
            <div>
              <h4 className="font-display text-xl font-normal leading-tight tracking-[-0.01em] text-content md:text-[1.4rem]">
                {f.name}
              </h4>
              <p className="mt-3 max-w-md text-[0.98rem] leading-relaxed text-mutedc">
                {f.desc}
              </p>
            </div>
          </div>
        );
      })}
      {/* bottom hairline to close the sheet */}
      <div className="col-span-1 border-t border-line md:col-span-2" />
    </div>
  );
}

export default function CaseStudy() {
  const { slug } = useParams();
  const c = CASES.find((x) => x.slug === slug);
  if (!c) {
    return (
      <div className="grid min-h-screen place-items-center bg-page px-6 text-center">
        <div>
          <p className="font-display text-3xl font-bold text-content">This loop doesn&apos;t exist.</p>
          <Link to="/" className="mt-4 inline-block text-accent-c underline">
            Back to RunLoop
          </Link>
        </div>
      </div>
    );
  }

  const d = c.detail;

  return (
    <div className="grain relative">
      <CaseNav />
      <main className="mx-auto w-full max-w-[1200px] px-6 pt-32 md:px-12 md:pt-40">
        {/* Header */}
        <Eyebrow>
          {c.caseNo} — {c.client}
        </Eyebrow>
        <h1 className="mt-8 font-display text-4xl font-light leading-[1.06] tracking-[-0.015em] text-content sm:text-5xl md:text-6xl">
          {c.teaserParts.map((p, i) =>
            p.em ? (
              <span key={i} className="emph">
                {p.t}
              </span>
            ) : (
              <span key={i}>{p.t}</span>
            )
          )}
        </h1>

        {/* Product preview image */}
        <FadeUp className="mt-14 md:mt-20">
          <div className="relative w-full overflow-hidden rounded-lg border border-line bg-surface">
            <img
              src="/capacite_product.png"
              alt="Capacité BD Intelligence dashboard — daily bulletin, live feed, sector breakdown"
              className="block h-auto w-full"
              loading="lazy"
            />
          </div>
        </FadeUp>

        {/* The problem */}
        <div className="mt-24 grid grid-cols-1 gap-8 md:mt-36 md:grid-cols-[0.3fr_0.7fr]">
          <Eyebrow>{d.problemEyebrow}</Eyebrow>
          <FadeUp>
            <p className="max-w-3xl text-xl leading-relaxed text-mutedc md:text-2xl">{d.problem}</p>
          </FadeUp>
        </div>

        {/* The problem behind the problem */}
        <div className="py-28 text-center md:py-40">
          <FadeUp>
            <div className="eyebrow justify-center">{d.behindEyebrow}</div>
          </FadeUp>
          <h2 className="mx-auto mt-8 max-w-4xl font-display text-3xl font-light leading-[1.18] tracking-tight text-content sm:text-4xl md:text-5xl">
            {d.behindParts.map((p, i) =>
              p.em ? (
                <span key={i} className="emph">
                  {p.t}
                </span>
              ) : (
                <span key={i}>{p.t}</span>
              )
            )}
          </h2>
        </div>

        {/* Continuity trail image */}
        <FadeUp>
          <div className="relative w-full overflow-hidden rounded-lg border border-line bg-surface">
            <img
              src="/capacite_continuity.png"
              alt="Continuity trail — announce root and current records linked over time"
              className="block h-auto w-full"
              loading="lazy"
            />
          </div>
        </FadeUp>

        {/* THE SYSTEM — features spec sheet */}
        {d.system && (
          <section className="mt-28 md:mt-40">
            <Eyebrow>{d.system.eyebrow}</Eyebrow>
            <FadeUp>
              <p className="mt-8 max-w-3xl text-lg leading-relaxed text-content md:text-xl">
                {d.system.intro}
              </p>
            </FadeUp>
            <FeatureList features={d.system.features} />
          </section>
        )}

        {/* Live feed screenshot — sits between the System spec sheet and End Goal */}
        <FadeUp className="mt-20 md:mt-24">
          <div className="relative w-full overflow-hidden rounded-lg border border-line bg-surface">
            <img
              src="/capacite_livefeed.png"
              alt="Capacité live feed — filtered tender records with geography, sector and value details"
              className="block h-auto w-full"
              loading="lazy"
            />
          </div>
        </FadeUp>

        {/* END GOAL — Closing the loop */}
        {d.endGoal && (
          <section className="mt-28 pb-32 md:mt-40 md:pb-40">
            <Eyebrow>{d.endGoal.eyebrow}</Eyebrow>
            <h2 className="mt-8 font-display text-4xl font-light leading-[1.04] tracking-[-0.02em] text-content sm:text-5xl md:text-6xl">
              {d.endGoal.titleParts.map((p, i) =>
                p.em ? (
                  <span key={i} className="emph italic">
                    {p.t}
                  </span>
                ) : (
                  <span key={i}>{p.t}</span>
                )
              )}
            </h2>
            <div className="mt-12 grid grid-cols-1 gap-6 md:mt-14 md:max-w-3xl">
              {d.endGoal.paragraphs.map((para, i) => (
                <FadeUp key={i} delay={i * 0.06}>
                  <p className="text-lg leading-relaxed text-content md:text-xl">{para}</p>
                </FadeUp>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
